import type { NotificationHydraItem } from "@verbleif/lib/src/composables/Api/Type/Notification/notification.interface";
import type { UserNotificationHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserNotification/userNotification.interface";
import { LoopbackFilterOperatorEnum } from "@verbleif/lib/src/composables/Api/Type/LoopbackFilter/LoopbackFilterOperator.enum";
import { createGlobalState } from "@vueuse/core";
import { useNotificationEndpoints } from "./Notifications/useNotificationEndpoints";
import { unreadNotificationsByEntityQueryOptions } from "./Notifications/v2/notifications.api";
import { fetchQueryOnce } from "./Query/fetchQueryOnce";
import { useResourceScope } from "./Query/resourceKeys";
import { useNavbarUnreadCount } from "./useNavbarUnreadCount";

type EntityType = "task" | "report";

/**
 * Marks notifications as read by task or report IRI
 * This is used when opening a task/report to automatically mark related notifications as read
 */
export const useMarkNotificationAsRead = createGlobalState(() => {
  const { checkUnreadCount } = useNavbarUnreadCount();
  const { isV2Enabled, collectionEndpoint, markAsRead } = useNotificationEndpoints();
  const scope = useResourceScope();

  /**
   * `isUserNotification` says which kind the id belongs to when the caller knows it (a push names its kind); without
   * it the active notification system decides.
   */
  async function markNotificationAsReadById(notificationId: number, isUserNotification?: boolean) {
    if (!notificationId) {
      return;
    }

    try {
      await markAsRead(notificationId, new Date().toISOString(), isUserNotification);
      await checkUnreadCount();
    } catch (error) {
      // Silently fail - we don't want to interrupt the user experience
      console.error(`[useMarkNotificationAsRead] Failed to mark notification ${notificationId} as read:`, error);
    }
  }

  // The legacy resource filters through the loopback syntax, the v2 inbox through API Platform's own filters.
  function unreadByEntityParams(entityIri: string, entityType: EntityType): URLSearchParams {
    const params = new URLSearchParams({ active: "true" });

    if (isV2Enabled.value) {
      params.set(entityType, entityIri);
      params.set("exists[readAt]", "false");
      return params;
    }

    params.set(`filter[where][${entityType}][${LoopbackFilterOperatorEnum.EQ}]`, entityIri);
    params.set(`filter[where][readAt][${LoopbackFilterOperatorEnum.NULL}]`, "");
    return params;
  }

  async function markNotificationsAsReadByEntity(entityIri: string, entityType: EntityType) {
    if (!collectionEndpoint.value || !entityIri) {
      return;
    }

    try {
      const data = await fetchQueryOnce(
        unreadNotificationsByEntityQueryOptions(
          scope.value,
          collectionEndpoint.value,
          unreadByEntityParams(entityIri, entityType),
        ),
      );

      const notificationIds = (data.member ?? [])
        .filter((notification: NotificationHydraItem | UserNotificationHydraItem) => Boolean(notification.id && !notification.readAt))
        .map((notification: NotificationHydraItem | UserNotificationHydraItem) => notification.id);

      if (notificationIds.length === 0) {
        return;
      }

      const readAt = new Date().toISOString();
      await Promise.all(notificationIds.map((notificationId: number) => markAsRead(notificationId, readAt)));
      await checkUnreadCount();
    } catch (error) {
      // Silently fail - we don't want to interrupt the user experience
      console.error(`[useMarkNotificationAsRead] Failed to mark notifications as read for ${entityType}:`, error);
    }
  }

  return {
    markNotificationAsReadById,
    markNotificationsAsReadByEntity,
  };
});
